import React, { ReactNode } from 'react';
import { Paper, PaperProps } from '@mui/material';
import { CSSProperties, styled, SxProps, Theme } from '@mui/material/styles';
import { roundedBorderBackground } from 'shared/theme';
import Anchor from './Anchor';

const StyledAnchor = styled(Anchor)({
  display: 'block',
  transition: 'all 0.2s ease-in',
  '&:hover': {
    textDecoration: 'none',
    transform: 'translateY(-0.25rem)',
  },
});

interface CardProps {
  backgroundColor?: string;
  borderColor?: string;
  children: ReactNode;
  className?: string;
  color?: string;
  component?: PaperProps['component'];
  fullBleed?: boolean;
  isActive?: boolean;
  noElevation?: boolean;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}

const Card: React.FC<CardProps> = ({
  backgroundColor,
  borderColor,
  children,
  className,
  fullBleed,
  color,
  component,
  isActive,
  noElevation,
  onClick,
  sx,
}: CardProps) => (
  <Paper
    className={className}
    component={component || 'section'}
    onClick={onClick}
    elevation={noElevation ? 0 : 1}
    sx={[(t) => ({
      background: borderColor
        ? roundedBorderBackground('left', borderColor)
        : backgroundColor || (isActive ? t.palette.backgroundFour : t.palette.common.white),
      border: isActive ? `1px solid ${t.palette.primary.main}` : 'none',
      borderRadius: `${t.shape.borderRadius}px`,
      color: color || t.palette.text.primary,
      outline: 'none',
      padding: fullBleed ? '1rem 0' : '1rem',
      textAlign: 'left',
    }), ...(Array.isArray(sx) ? sx : [sx])]}
  >
    {children}
  </Paper>
);

interface CardBaseProps {
  anchorClassName?: string;
  backgroundColor?: string;
  borderColor?: string;
  children: ReactNode;
  className?: string;
  color?: string;
  fullBleed?: boolean;
  href?: string;
  isActive?: boolean;
  isEdit?: boolean;
  noElevation?: boolean;
  component?: PaperProps['component'];
  onClick?: () => void;
  style?: CSSProperties;
  sx?: SxProps<Theme>;
}

const CardBase: React.FC<CardBaseProps> = (props: CardBaseProps) => {
  const {
    anchorClassName, className, href, isActive, isEdit,
  } = props;

  const link: Link = {
    link: href || '',
    type: href?.toLowerCase().indexOf('http') === 0 ? 'url' : 'route',
  };

  return href ? (
    <StyledAnchor
      className={anchorClassName}
      link={link}
      target={link.type === 'url' ? '_blank' : '_self'}
    >
      <Card {...props} isActive={isActive} />
    </StyledAnchor>
  ) : (
    <Card
      {...props}
      isActive={isActive}
      className={`${className} ${isEdit ? 'fullHeight' : 'marginBottom'}`}
    />
  );
};

export default CardBase;
